import React, { useState, useEffect, useRef } from 'react';
import client from '../services/api';

export default function PropertySearchWidget({ value, onChange }) {
  const [query, setQuery]           = useState('');
  const [results, setResults]       = useState([]);
  const [status, setStatus]         = useState('idle');   // idle | local | places | done
  const [showDropdown, setShowDropdown] = useState(false);
  const [noLocalResults, setNoLocalResults] = useState(false);
  const containerRef = useRef(null);

  // Debounced local search as user types
  useEffect(() => {
    if (value) return; // already selected
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setNoLocalResults(false);
      setStatus('idle');
      return;
    }
    const timer = setTimeout(async () => {
      setStatus('local');
      try {
        const res = await client.get('/properties/search', { params: { q } });
        const props = res.data.properties || [];
        setResults(props);
        setNoLocalResults(props.length === 0);
        setShowDropdown(true);
      } catch {
        setResults([]);
        setNoLocalResults(false);
      }
      setStatus('done');
    }, 400);
    return () => clearTimeout(timer);
  }, [query, value]);

  // Close dropdown on outside click
  useEffect(() => {
    function onClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  async function searchOnline() {
    setStatus('places');
    setNoLocalResults(false);
    try {
      const res = await client.get('/properties/search', {
        params: { q: query.trim(), fallback: 'true' },
      });
      const props = res.data.properties || [];
      setResults(props);
      setNoLocalResults(props.length === 0);
      setShowDropdown(true);
    } catch {
      setResults([]);
    }
    setStatus('done');
  }

  function selectProperty(property) {
    onChange(property);
    setShowDropdown(false);
    setQuery('');
    setResults([]);
    setNoLocalResults(false);
  }

  function clearSelection() {
    onChange(null);
    setQuery('');
    setResults([]);
    setStatus('idle');
    setNoLocalResults(false);
  }

  // Show selected state
  if (value) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 0.85rem', border: '1.5px solid var(--brand)', borderRadius: 'var(--radius-sm)', background: 'var(--brand-light)' }}>
        {value.image_urls?.[0] && (
          <img src={value.image_urls[0]} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 'var(--radius-sm)', flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 'var(--text-base)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value.name}</div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value.address || value.location}</div>
        </div>
        <button
          type="button"
          onClick={clearSelection}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--brand)', fontWeight: 600, flexShrink: 0, padding: '0.2rem 0' }}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder="Search resort name or address…"
          autoComplete="off"
          style={{ flex: 1 }}
        />
        {status === 'local' || status === 'places' ? (
          <span style={{ alignSelf: 'center', fontSize: 'var(--text-sm)', color: 'var(--ink-muted)', whiteSpace: 'nowrap', paddingRight: '0.25rem' }}>
            {status === 'places' ? 'Searching online…' : 'Searching…'}
          </span>
        ) : null}
      </div>

      {/* No local results — offer Places fallback */}
      {noLocalResults && status === 'done' && query.trim().length >= 2 && (
        <div style={{ marginTop: '0.35rem', fontSize: 'var(--text-sm)', color: 'var(--ink-muted)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Not found locally.{' '}
          <button
            type="button"
            onClick={searchOnline}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand)', fontWeight: 600, fontSize: 'var(--text-sm)', padding: 0 }}
          >
            Search Google Places →
          </button>
        </div>
      )}

      {/* Results dropdown */}
      {showDropdown && results.length > 0 && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-md)', margin: '2px 0 0', padding: 0,
          listStyle: 'none', maxHeight: 360, overflowY: 'auto',
        }}>
          {results.map(p => (
            <li
              key={p.id}
              onClick={() => selectProperty(p)}
              style={{
                padding: '0.65rem 0.85rem',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', gap: '0.65rem',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-alt)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {p.image_urls?.[0] ? (
                <img src={p.image_urls[0]} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 'var(--radius-sm)', flexShrink: 0 }} />
              ) : (
                <div style={{ width: 44, height: 44, background: 'var(--sand)', borderRadius: 'var(--radius-sm)', flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.address || p.location}</div>
              </div>
              {p.rating && (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--warning)', fontWeight: 600, flexShrink: 0 }}>★ {Number(p.rating).toFixed(1)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
